// src/pages/skills/controllers/Skills.controller.tsx
import { useState, useEffect } from "react";
import i18n from "../../../i18n/i18n";
import { SkillsViewProps } from "../types";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";
import { useSkills } from "../../../hooks/useSkills";

export function SkillsController(): SkillsViewProps {
  useDocumentTitle(
    "Skills",
    "Habilidades técnicas: React, TypeScript, Node.js, MongoDB, Cloud (AWS/Azure), Docker, AI/ML."
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { skills: apiSkills, loading: skillsLoading } = useSkills({ limit: 100 });

  // Simula carregamento inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);
  // Categorias de habilidades
  const categories = [
    {
      id: 1,
      name: "Frontend",
      description:
        "Tecnologias para desenvolvimento de interfaces e experiência do usuário",
      icon: "ph-code",
    },
    {
      id: 2,
      name: "Backend",
      description: "Tecnologias para desenvolvimento de servidores e APIs",
      icon: "ph-database",
    },
    {
      id: 3,
      name: "Mobile",
      description: "Tecnologias para desenvolvimento de aplicativos móveis",
      icon: "ph-device-mobile",
    },
    {
      id: 4,
      name: "DevOps",
      description: "Tecnologias para infraestrutura e CI/CD",
      icon: "ph-cloud",
    },
    {
      id: 5,
      name: "AI/ML",
      description:
        "Tecnologias para inteligência artificial e aprendizado de máquina",
      icon: "ph-brain",
    },
  ]; // Lista de habilidades
  const skills = [
    {
      id: 1,
      name: "React",
      level: 100,
      category: "Frontend",
      icon: "code", // Usando um ícone mais comum do Phosphor
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.react",
        "Desenvolvimento de interfaces de usuário modernas e reativas com React e seu ecossistema (Redux, Context API, Hooks)."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 2,
      name: "TypeScript",
      level: 100,
      category: "Frontend",
      icon: "file-code",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.typescript",
        "Desenvolvimento de aplicações web fortemente tipadas para maior robustez e manutenibilidade."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 3,
      name: "Node.js",
      level: 100,
      category: "Backend",
      icon: "code",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.node",
        "Criação de serviços e APIs RESTful com Express, autenticação JWT e integrações com bancos de dados."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 4,
      name: "MongoDB",
      level: 100,
      category: "Backend",
      icon: "database",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.mongodb",
        "Modelagem de dados NoSQL, queries complexas e otimização de performance."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 5,
      name: "Cloud",
      level: 100,
      category: "DevOps",
      icon: "cloud",
      logo: "/assets/logos/cloud.svg",
      description: (i18n as any).t(
        "skills:descriptions.cloud",
        "Proficiência em computação em nuvem com experiência em AWS e Azure. AWS: EC2, S3, Lambda, API Gateway. Azure: App Service, Functions, Storage, API Management."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 6,
      name: "Docker",
      level: 100,
      category: "DevOps",
      icon: "whale",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.docker",
        "Conteinerização de aplicações para ambientes de desenvolvimento e produção consistentes."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 7,
      name: "TensorFlow",
      level: 100,
      category: "AI/ML",
      icon: "brain",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.tensorflow",
        "Desenvolvimento de modelos de deep learning para processamento de linguagem natural e visão computacional."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 8,
      name: "PyTorch",
      level: 100,
      category: "AI/ML",
      icon: "lightning",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.pytorch",
        "Implementação de redes neurais para análise de dados e pesquisa em aprendizado profundo."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 9,
      name: "React Native",
      level: 100,
      category: "Mobile",
      icon: "device-mobile",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.reactNative",
        "Desenvolvimento de aplicativos móveis multiplataforma com React Native e Expo."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 10,
      name: "Python",
      level: 100,
      category: "AI/ML",
      icon: "code",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
      description: (i18n as any).t(
        "skills:descriptions.python",
        "Desenvolvimento de scripts, análise de dados e prototipação de modelos de machine learning com Python (Pandas, NumPy, scikit-learn)."
      ),
      yearsOfExperience: 5,
    },
    {
      id: 12,
      name: "Jest & Testing Library",
      level: 100,
      category: "Frontend",
      icon: "test-tube",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg",
      description: (i18n as any).t(
        "skills:descriptions.testing",
        "Testes unitários e de integração para garantir robustez de aplicações frontend."
      ),
      yearsOfExperience: 5,
    },
  ];

  const apiMapped = apiSkills.map((s: any, idx: number) => ({
    id: idx + 1,
    name: s.name,
    level: typeof s.level === "number" ? s.level * 20 : 100,
    category: s.category,
    icon: "code",
    logo: s.logo,
    description: s.description,
    yearsOfExperience: s.years,
  }));

  const finalSkills = apiMapped.length > 0 ? apiMapped : skills;

  return {
    isLoading: isLoading || skillsLoading,
    skills: finalSkills,
    categories,
    featuredProjects: [],
    selectedCategory,
    setSelectedCategory,
  };
}

export default SkillsController;
